import { ROLE } from "constant/app";
import { PropSelectOptions } from "constant/prop-types";
import { Field, Form, Formik } from "formik";
import noop from "lodash/noop";
import PropTypes from "prop-types";
import React from "react";
import { Row, Col, FormGroup, FormLabel as BsFormLabel, FormText } from "react-bootstrap";
import { Redirect } from "react-router-dom";
import * as User from "redux-store/users/selectors";
import { FormLabel } from "ui/form-label";
import { SelectField } from "ui/formik/select-field";
import { StringField } from "ui/formik/string-field";
import { getSelectedOpts } from "util/select";
import { sortByLabelAsc } from "util/sort-by-prop";
import { VIEW } from "view/views";

class UserDetailForm extends React.PureComponent {
  static get propTypes() {
    return {
      submitted: PropTypes.bool.isRequired,
      networkOptions: PropSelectOptions,
      type: PropTypes.oneOf([VIEW.CREATE, VIEW.EDIT]).isRequired,
      user: PropTypes.object,
      currentUser: PropTypes.object,
      featureFlags: PropTypes.object,
    };
  }

  constructor(props) {
    super(props);

    const { role = null } = props.user;

    this.state = {
      role,
    };

    this.setRef = this.setRef.bind(this);
    this.updateRole = this.updateRole.bind(this);

    this.form = null;
    this.roleOptions = this._getRoleOptions(props.type);
  }

  _getInitialValues(netSelectEnabled) {
    const { networkOptions, user } = this.props;
    const { email = "", name = "" } = user;

    const role = this.roleOptions.find((o) => o.value === user.role) || null;
    const networks = getSelectedOpts(networkOptions, user.networks);

    return {
      email,
      name,
      role,
      networks: netSelectEnabled ? networks : null,
    };
  }

  _getRoleOptions(viewType) {
    const { CREATE, EDIT } = VIEW;
    const { featureFlags } = this.props;

    const roleOpts = [
      {
        detail: "Full control of the account.",
        label: "Full Access",
        value: ROLE.ADMIN,
        views: new Set([CREATE, EDIT]),
      },
      {
        detail: "Full control of the selected networks.",
        label: "Operator",
        value: ROLE.OPERATOR,
        views: new Set([CREATE, EDIT]),
      },
      {
        detail: "Read-only access to the selected network.",
        label: "Read Only",
        value: ROLE.READ_ONLY,
        views: new Set([CREATE, EDIT]),
      },
      {
        detail: "No access to the account; if you re-activate, password reset is required.",
        label: "Disabled",
        value: ROLE.DISABLED,
        views: new Set([EDIT]),
      },
      {
        detail: "Full control of the account, except managing other users.",
        label: "Admin Operator",
        value: ROLE.ADMIN_OPERATOR,
        views: new Set([CREATE, EDIT]),
      },
    ];

    return roleOpts
      .filter((opt) => opt.views.has(viewType))
      .sort(sortByLabelAsc);
  }

  formatRole(opt) {
    return (
      <span>
        {opt.label}
        <span className="ml-4 font-smaller text-muted"> ({opt.detail})</span>
      </span>
    );
  }

  getFormValues() {
    const formikBag = this.form.getFormikBag();
    const { values } = formikBag;

    if (this.isNetSelectEnabled()) {
      return values;
    }

    return {
      ...values,
      networks: null,
    };
  }

  isNetSelectEnabled() {
    const { role } = this.state;

    return [ROLE.OPERATOR, ROLE.READ_ONLY].includes(role);
  }

  setRef(el) {
    if (el) {
      this.form = el;
    }
  }

  updateRole(roleOpt) {
    this.setState({ role: roleOpt ? roleOpt.value : null });
  }

  render() {
    const { networkOptions, submitted, type, currentUser } = this.props;

    const netSelectEnabled = this.isNetSelectEnabled();
    const initialValues = this._getInitialValues(netSelectEnabled);

    if (!User.isFullAdmin(currentUser)) {
      return <Redirect to="/404" />;
    }

    return (
      <Formik initialValues={initialValues} onSubmit={noop} ref={this.setRef}>
        <Form>
          <Row>
            <Col xs={12}>
              <FormGroup controlId="name">
                <FormLabel as={BsFormLabel} required={type === VIEW.CREATE}>
                  Name
                </FormLabel>
                <Field
                  name="name"
                  component={StringField}
                  placeholder="Apollo Creed"
                  readOnly={submitted || type !== VIEW.CREATE}
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          {type === VIEW.CREATE && (
            <Row>
              <Col xs={12}>
                <FormGroup controlId="email">
                  <FormLabel as={BsFormLabel} required>
                    Email
                  </FormLabel>
                  <Field
                    name="email"
                    component={StringField}
                    placeholder="user@example.com"
                    readOnly={submitted}
                    type="email"
                    required
                  />
                  <FormText>
                    Invitation will be sent to this email address.
                  </FormText>
                </FormGroup>
              </Col>
            </Row>
          )}

          <Row>
            <Col xs={12}>
              <FormGroup controlId="role">
                <FormLabel as={BsFormLabel} required>
                  Access Level
                </FormLabel>
                <Field
                  name="role"
                  component={SelectField}
                  formatOptionLabel={this.formatRole}
                  options={this.roleOptions}
                  onChange={this.updateRole}
                  placeholder="Select User Role"
                  readOnly={submitted}
                  required
                />
                <FormText>Please select the user access level.</FormText>
              </FormGroup>
            </Col>
          </Row>

          {netSelectEnabled && (
            <Row>
              <Col xs={12}>
                <FormGroup controlId="networks">
                  <FormLabel as={BsFormLabel} required>
                    Target networks
                  </FormLabel>
                  <Field
                    name="networks"
                    component={SelectField}
                    options={networkOptions}
                    placeholder="Select Networks"
                    readOnly={submitted}
                    isMulti
                    required
                  />
                  <FormText>
                    Select at least one network for this user to manage.
                  </FormText>
                </FormGroup>
              </Col>
            </Row>
          )}
        </Form>
      </Formik>
    );
  }
}

export { UserDetailForm };
